import { flag } from './rowParts';

/**
 * The advert form's data rules (UN-3248), kept out of the component so they can be tested without a
 * DOM: the wire mapping, the Not-Visible lock, tag de-dupe and the request body.
 */

// AppAdvertPlacement (app-store-core). Validated service-side, so an unknown value is a 400 — the
// form only ever offers these five, narrowed by the linked app (below).
export const PLACEMENTS = [
  { value: 'APP_MENU', name: 'App Menu' },
  { value: 'PAYMENT_PROVIDER', name: 'Payment Provider' },
  { value: 'SHIPPING_CARRIER', name: 'Shipping Carrier' },
  { value: 'MARKETING_APP', name: 'Marketing App' },
  { value: 'CATALOG', name: 'Catalog' }
];

/**
 * The placements the form offers, tailored to the linked app: the server sends `allowedPlacements`
 * on every app payload (App Menu plus the slot native to the app's source — a shipping carrier sits
 * under App Menu or Shipping Carrier, a module only under App Menu, an extension anywhere). Without
 * an app, or with an older server that sends none, every placement stays available.
 */
export const placementOptions = app =>
  app && Array.isArray(app.allowedPlacements) && app.allowedPlacements.length
    ? PLACEMENTS.filter(p => app.allowedPlacements.includes(p.value))
    : PLACEMENTS;

/**
 * The placement to hold once `app` is the linked app: the current one when the app allows it,
 * otherwise the app's native slot (the server snaps the same way on link), falling back to App Menu.
 */
export const tailorPlacement = (placement, app) => {
  const allowed = placementOptions(app).map(p => p.value);
  if (allowed.includes(placement)) return placement;
  // allowed is never empty (it falls back to every placement), so its first entry is always on offer
  return app && app.nativePlacement && allowed.includes(app.nativePlacement) ? app.nativePlacement : allowed[0];
};

// AppType. Editable only while the advert is unlinked; once linked it is derived from the app.
export const TYPES = [
  { value: 'INTEGRATION', name: 'Integration' },
  { value: 'BUILT_IN', name: 'Built-in' },
  { value: 'MODULE', name: 'Module' },
  { value: 'EXTENSION', name: 'Extension' }
];

// ErpCompatibility.
export const ERPS = [
  { value: 'SAGE_300', label: 'Sage 300' },
  { value: 'SAGE_X3', label: 'Sage X3' },
  { value: 'BUSINESS_CENTRAL', label: 'Business Central' }
];

/** Flags that Not Visible locks (Data Model §5). Preinstalled is deliberately absent — it stays live. */
export const LOCKED_BY_NOT_VISIBLE = ['featured', 'mostPopular', 'publicListed'];

export const EMPTY_FORM = {
  // The linked app, held in the form so one value drives three rules: the module picker's selection,
  // whether Type is derived (and so read-only), and whether create sends appUuid.
  appUuid: null,
  name: '',
  description: '',
  fullDescription: '',
  placement: 'APP_MENU',
  type: '',
  monthlyPrice: '',
  imageUrl: '',
  author: '',
  publishedVersion: '',
  erpCompatibility: [],
  tags: [],
  // The gallery (UN-3334): [{ kind: 'IMAGE' | 'VIDEO', url, contentType }], in display order.
  assets: [],
  featured: false,
  mostPopular: false,
  publicListed: false,
  preinstalled: false,
  notVisible: false
};

const list = value => (Array.isArray(value) ? value : []);
const text = value => (value === null || value === undefined ? '' : String(value));

/** What the App Store card's two-line description box shows in full; the detail dialog shows the Full Description. */
export const MAX_DESCRIPTION = 50;

/**
 * Why the form cannot be saved as it stands, keyed by field; empty when it can. App Name is the field the server
 * also refuses blank (app-advert-name-required). The description ceiling is the form's alone: the API keeps 512,
 * so an older advert or a catalog pre-fill still loads, and is held here until it is shortened. Publish's other
 * requirement, a linked module, is enforced by the disabled Publish button.
 */
export const formErrors = form => {
  const errors = {};
  if (!form.name.trim()) {
    errors.name = 'App Name is required.';
  }
  if (form.description.length > MAX_DESCRIPTION) {
    errors.description = `Shorten the description to ${MAX_DESCRIPTION} characters, which is what the App Store card shows.`;
  }
  return errors;
};

/** The gallery's ceilings (UN-3334), mirrored from AppAdvertAuthoringService — the server enforces them too. */
export const MAX_ASSETS = 12;
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const MAX_VIDEO_BYTES = 50 * 1024 * 1024;
export const ASSET_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,video/mp4,video/webm';

export const isVideo = asset => !!asset && asset.kind === 'VIDEO';

/**
 * The poster source for a video asset. Safari decodes no frame for `preload="metadata"` and paints the element
 * black until playback starts, so a `#t=0.1` media fragment asks it to seek just past the start and show that
 * frame instead (review on #283). Chrome and Firefox already render frame 0; the fragment is a no-op there.
 */
export const posterSrc = url => (url && !url.includes('#') ? `${url}#t=0.1` : url);

/** Detail `assets` -> form gallery: by stored position, only the fields the form sends back. */
const assetsFromDto = value =>
  list(value)
    .slice()
    .sort((a, b) => (a.position || 0) - (b.position || 0))
    .map(asset => ({ kind: asset.kind, url: asset.url, contentType: asset.contentType || null }));

/**
 * Appends an uploaded asset, or returns the same array when it would break a rule the server also
 * enforces: at most MAX_ASSETS, at most one video. Same-reference return is the "refused" signal.
 */
export const addAsset = (assets, asset) => {
  if (!asset || !asset.url) return assets;
  if (assets.length >= MAX_ASSETS) return assets;
  if (isVideo(asset) && assets.some(isVideo)) return assets;
  return [...assets, { kind: asset.kind, url: asset.url, contentType: asset.contentType || null }];
};

export const removeAsset = (assets, index) => assets.filter((_, i) => i !== index);

/**
 * Which of a picked batch may be uploaded, and why the rest were skipped (review on #283): one preview video,
 * at most MAX_ASSETS in total, and the size ceilings the help text promises — all decided BEFORE anything is
 * uploaded, so a 60 MB clip is refused in the browser instead of after a full upload. The server enforces the
 * same three rules; this only saves the round trip and names the file.
 */
export const planAssetBatch = (existing, files) => {
  const notes = [];
  const accepted = [];
  let hasVideo = existing.some(isVideo);
  files.forEach(file => {
    const video = String(file.type || '').startsWith('video/');
    if (video && hasVideo) {
      notes.push(`${file.name} skipped — one preview video per advert.`);
      return;
    }
    if (existing.length + accepted.length >= MAX_ASSETS) {
      notes.push(`${file.name} skipped — up to ${MAX_ASSETS} assets.`);
      return;
    }
    const limit = video ? MAX_VIDEO_BYTES : MAX_IMAGE_BYTES;
    if (typeof file.size === 'number' && file.size > limit) {
      notes.push(`${file.name} skipped — ${video ? 'video is' : 'images are'} limited to ${limit / (1024 * 1024)} MB.`);
      return;
    }
    if (video) hasVideo = true;
    accepted.push(file);
  });
  return { accepted, notes };
};

/** Moves the asset at `from` to `to`; out-of-range or same slot leaves the array as it is. */
export const moveAsset = (assets, from, to) => {
  if (from === to || from < 0 || to < 0 || from >= assets.length || to >= assets.length) return assets;
  const next = assets.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

/**
 * Detail payload -> form state. The five checklist booleans reach the wire is-prefixed (the platform
 * JacksonConfig rewrites boolean accessors, see rowParts.flag), which is why none of them can be read
 * off the DTO by their plain names.
 */
export const fromDto = dto => ({
  ...EMPTY_FORM,
  appUuid: dto.appUuid || null,
  name: text(dto.name),
  description: text(dto.description),
  fullDescription: text(dto.fullDescription),
  placement: dto.placement || 'APP_MENU',
  type: text(dto.type),
  monthlyPrice: dto.monthlyPrice === null || dto.monthlyPrice === undefined ? '' : String(dto.monthlyPrice),
  imageUrl: text(dto.imageUrl),
  author: text(dto.author),
  publishedVersion: text(dto.publishedVersion),
  erpCompatibility: list(dto.erpCompatibility),
  tags: list(dto.tags),
  assets: assetsFromDto(dto.assets),
  featured: flag(dto, 'featured'),
  mostPopular: flag(dto, 'mostPopular'),
  publicListed: flag(dto, 'publicListed'),
  preinstalled: flag(dto, 'preinstalled'),
  notVisible: flag(dto, 'notVisible')
});

/**
 * Pre-fill for "Create advert" from the All Apps tab. The server pre-fills the same fields from the
 * app row on create, but only where the request leaves them null — filling them here as well is what
 * makes the live preview show something before the first save.
 *
 * Fed from the app DETAIL payload (AppDto), not the All Apps row: description and imageUrl are the
 * source-reported pre-fill fields and the list projection does not carry them.
 */
export const prefillFromApp = app => ({
  ...EMPTY_FORM,
  appUuid: app.uuid,
  name: text(app.name),
  description: text(app.description),
  imageUrl: text(app.imageUrl),
  type: text(app.type),
  author: text(app.author),
  // A brand-new advert for a carrier starts in the carrier slot; the form still offers App Menu.
  placement: app.nativePlacement ? tailorPlacement(app.nativePlacement, app) : EMPTY_FORM.placement
});

/**
 * Not Visible wins over the other visibility flags (CQ-3): it hides the advert outright, so Featured,
 * Most Popular and Public are forced off as well as greyed. The server applies the same lock after its
 * merge — doing it here too keeps the checkboxes honest about what will be saved instead of leaving
 * three ticks on screen that the next reload silently clears.
 */
export const applyVisibilityLock = form =>
  form.notVisible ? { ...form, featured: false, mostPopular: false, publicListed: false } : form;

/** LISTED / UNLISTED / HIDDEN, mirroring AdvertVisibility.of() so the form previews its own state. */
export const visibilityOf = form => {
  if (form.notVisible) return 'HIDDEN';
  return form.publicListed ? 'LISTED' : 'UNLISTED';
};

/**
 * Case-insensitive de-dupe (AC5): typing "CRM" when "crm" is already a chip adds nothing. The first
 * spelling wins — re-adding a tag is not an instruction to re-case the existing one.
 */
export const addTag = (tags, raw) => {
  const tag = (raw || '').trim();
  if (!tag) return tags;
  const existing = tags.some(t => t.toLowerCase() === tag.toLowerCase());
  return existing ? tags : [...tags, tag];
};

export const removeTag = (tags, tag) => tags.filter(t => t !== tag);

/**
 * Form state -> AppAdvertRequest. Two things are not obvious:
 *
 * - The five booleans are sent under their plain names. AppAdvertRequest pins them with @JsonProperty
 *   precisely so a client can, because without it the host's is-prefix rewrite would ignore
 *   {"featured": false} as an unknown property and merge() would keep the old value.
 * - `type` is omitted once the advert is linked. The server treats a type that differs from the app's
 *   as an error (app-advert-type-locked) and the field is read-only in that state anyway.
 *
 * A blank Monthly Price is omitted rather than sent as null, because the API's merge semantics read
 * null as "keep the current value" — there is no clear-the-price wire value. Blank therefore means
 * "unchanged" on an advert that already has a price, which is what the disabled-looking empty field
 * would otherwise promise falsely.
 *
 * `appUuid` rides along on create only; afterwards the link is owned by link/unlink.
 */
export const toRequest = (form, { create = false } = {}) => {
  const locked = applyVisibilityLock(form);
  const body = {
    name: locked.name,
    description: locked.description,
    fullDescription: locked.fullDescription,
    placement: locked.placement,
    // Blank clears the image server-side (setImageUrl treats blank as null), so '' is meaningful here.
    imageUrl: locked.imageUrl,
    author: locked.author,
    publishedVersion: locked.publishedVersion,
    erpCompatibility: locked.erpCompatibility,
    tags: locked.tags,
    // Positions are the list order — a reorder in the form is just a resubmission (the server renumbers anyway).
    assets: (locked.assets || []).map((asset, position) => ({
      kind: asset.kind,
      url: asset.url,
      contentType: asset.contentType || null,
      position
    })),
    featured: locked.featured,
    mostPopular: locked.mostPopular,
    publicListed: locked.publicListed,
    preinstalled: locked.preinstalled,
    notVisible: locked.notVisible
  };
  if (!locked.appUuid && locked.type) {
    body.type = locked.type;
  }
  if (String(locked.monthlyPrice).trim() !== '') {
    body.monthlyPrice = Number(locked.monthlyPrice);
  }
  if (create && locked.appUuid) {
    body.appUuid = locked.appUuid;
  }
  return body;
};

/** Backfilled ledger rows carry unknown fields as null; the table shows an em dash rather than "null". */
export const dash = value => (value === null || value === undefined || value === '' ? '—' : value);

/**
 * The values of `field` that still exist once the other filter has been applied (UN-3372 item 8).
 *
 * The advert form's two module filters — by type and by source — each listed every value in the whole catalog,
 * independently of the other. Picking a type therefore kept offering sources that had no app of that type, and
 * choosing one emptied the picker, which reads as a broken catalog rather than as an impossible combination.
 * Deriving each list from the apps the OTHER filter leaves standing means every option returns at least one
 * module.
 */
export const facetOptions = (apps, field, otherField, otherValue) => [
  ...new Set(
    (apps || [])
      .filter(app => !otherValue || app[otherField] === otherValue)
      .map(app => app[field])
      .filter(Boolean)
  )
].sort();
